/* eslint-disable react/jsx-props-no-spreading */
import React from 'react';
import { useParams } from 'react-router-dom';

import { useArticlesByIds, useTag } from '@api/hooks';
import { ArticleTagField, AddSelectEntity, ArticleSelect, ModeSelect, ContentModuleModal } from '@molecules';
import { NO_MARGIN_BOTTOM } from '@constants';

import Form from '../Form';

import { FORM_FIELD, FORM_FIELDS, INITIAL_VALUES, MODE, MODES } from './constants';
import ArticleListField from './ArticleListField';
import Item from './Item';

const NewsCarouselModal = ({
  visible,
  hide,
  previewImg,
  onDelete,
  onSubmit,
  initialValues: data = INITIAL_VALUES,
  errors,
}) => {
  const [form] = Form.useForm(FORM_FIELDS);
  const { locale } = useParams();

  const articlesQuery = useArticlesByIds(
    data.slides?.map(({ articleId }) => articleId),
    locale,
    { passErrors: true },
  );
  React.useEffect(() => {
    if (visible && articlesQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.ARTICLES.name]: articlesQuery.articles });
    }
  }, [articlesQuery.articles, articlesQuery.status, form, visible]);

  const tagQuery = useTag(data.tagId);
  React.useEffect(() => {
    if (visible && tagQuery.status === 'success') {
      form.setFieldsValue({ [FORM_FIELD.TAG.name]: tagQuery.data });
    }
  }, [form, tagQuery.data, tagQuery.status, tagQuery.tag, visible]);

  const [mode, setMode] = React.useState();
  React.useEffect(() => {
    if (visible) {
      setMode(data.tagId ? MODE.AUTOMATIC.value : MODE.MANUAL.value);
    }
  }, [data.tagId, visible]);

  const initialValues = React.useMemo(() => form.deserialize(data), [data, form]);

  const handleClose = () => {
    form.resetFields();
    hide();
  };

  const handleSubmit = (values) => {
    onSubmit(values);
    handleClose();
  };

  const handleAddArticle = async (article) => {
    // can be undefined if only tag was saved to API
    const articles = form.getFieldValue(FORM_FIELD.ARTICLES.name) || [];
    form.setFieldsValue({ [FORM_FIELD.ARTICLES.name]: [...articles, article] });
  };

  return (
    <ContentModuleModal
      visible={visible}
      title="News carousel"
      subtitle="Choose up to 30 articles to appear in a featured news carousel. Any articles you’ve chosen for the hero carousel won’t be available to use here."
      okText={onDelete ? 'SAVE CHANGES' : 'ADD NEWS CAROUSEL'}
      onCancel={handleClose}
      onSubmit={form.submit}
      onDelete={onDelete}
      heroImageSrc={previewImg}
      errors={errors}
    >
      <ModeSelect onSelect={setMode} mode={mode} modes={MODES} />
      <Form
        form={form}
        onSerializedFinish={handleSubmit}
        initialValues={initialValues}
        layout="vertical"
        name="newsCarousel"
      >
        {mode === MODE.MANUAL.value && (
          <>
            <AddSelectEntity
              label="Title:"
              addText="ADD ARTICLE"
              onAdd={handleAddArticle}
              select={ArticleSelect}
              isTypeFilter
            />
            <ArticleListField form={form} style={NO_MARGIN_BOTTOM} isTypeFilter />
          </>
        )}
        {mode === MODE.AUTOMATIC.value && <ArticleTagField {...FORM_FIELD.TAG} style={NO_MARGIN_BOTTOM} />}
      </Form>
    </ContentModuleModal>
  );
};

NewsCarouselModal.Item = Item;

export default NewsCarouselModal;
